import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { useRestaurant } from "../contexts/RestaurantContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ChartBar, Users, Table, UsersFour, CalendarBlank, TrendUp, Clock, Medal } from "@phosphor-icons/react";

export default function EndOfServicePage() {
  const { restId } = useRestaurant();
  const [snapshot, setSnapshot] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");

  const fetchHistory = useCallback(async () => {
    try {
      const params = restId ? `?restaurant_id=${restId}` : "";
      const { data } = await api.get(`/service/snapshot-history${params}&limit=30`);
      setHistory(data);
      if (data.length > 0 && !selectedDate) {
        setSelectedDate(data[0].date);
      }
    } catch { /* silent */ }
  }, [restId, selectedDate]);

  const fetchSnapshot = useCallback(async () => {
    if (!selectedDate) return;
    try {
      const params = restId ? `&restaurant_id=${restId}` : "";
      const { data } = await api.get(`/service/snapshot?date=${selectedDate}${params}`);
      setSnapshot(data);
    } catch { /* silent */ }
  }, [selectedDate, restId]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);
  useEffect(() => { fetchSnapshot(); }, [fetchSnapshot]);

  const s = snapshot?.summary || {};
  const members = snapshot?.member_analytics || {};
  const tables = snapshot?.table_analytics || [];
  const servers = snapshot?.server_analytics || [];
  const tiers = members.tier_breakdown || {};

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="end-of-service-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans", color: "#1C1917" }}>End of Service Analytics</h2>
          <p className="text-xs text-stone-500 mt-1">
            <Badge className="bg-red-100 text-red-700 text-[10px] mr-1">Admin Only</Badge>
            Historical service records
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CalendarBlank size={16} className="text-stone-400" />
          <Select value={selectedDate} onValueChange={setSelectedDate}>
            <SelectTrigger className="w-[180px]" data-testid="date-selector">
              <SelectValue placeholder="Select date" />
            </SelectTrigger>
            <SelectContent>
              {history.map((h) => (
                <SelectItem key={h.date} value={h.date}>{h.date}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!snapshot ? (
        <div className="text-center py-16">
          <ChartBar size={48} className="mx-auto mb-3 text-stone-300" weight="duotone" />
          <p className="text-stone-500 text-sm">{history.length === 0 ? "No service records yet. End a service to generate analytics." : "Select a date to view analytics."}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg border border-stone-200 p-4 text-center">
              <Users size={20} className="mx-auto mb-1 text-stone-400" weight="duotone" />
              <p className="text-2xl font-bold" style={{ color: "#2F4F4F", fontFamily: "Work Sans" }}>{s.total_covers || 0}</p>
              <p className="text-[10px] text-stone-500 uppercase">Total Covers</p>
            </div>
            <div className="bg-white rounded-lg border border-stone-200 p-4 text-center">
              <CalendarBlank size={20} className="mx-auto mb-1 text-stone-400" weight="duotone" />
              <p className="text-2xl font-bold" style={{ color: "#F4A261", fontFamily: "Work Sans" }}>{s.total_reservations || 0}</p>
              <p className="text-[10px] text-stone-500 uppercase">Reservations</p>
            </div>
            <div className="bg-white rounded-lg border border-stone-200 p-4 text-center">
              <Clock size={20} className="mx-auto mb-1 text-stone-400" weight="duotone" />
              <p className="text-2xl font-bold" style={{ color: "#81B29A", fontFamily: "Work Sans" }}>{s.avg_turn_minutes || 0}m</p>
              <p className="text-[10px] text-stone-500 uppercase">Avg Turn</p>
            </div>
            <div className="bg-white rounded-lg border border-stone-200 p-4 text-center">
              <TrendUp size={20} className="mx-auto mb-1 text-stone-400" weight="duotone" />
              <p className="text-2xl font-bold" style={{ color: "#E07A5F", fontFamily: "Work Sans" }}>{s.service_duration_minutes || 0}m</p>
              <p className="text-[10px] text-stone-500 uppercase">Service Duration</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Member Analytics */}
            <div className="bg-white rounded-lg border border-stone-200 p-5" data-testid="member-analytics">
              <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-4 flex items-center gap-2">
                <Users size={16} weight="duotone" /> Member Analytics
              </h3>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="text-center p-3 rounded-lg bg-stone-50">
                  <p className="text-xl font-bold" style={{ color: "#2F4F4F" }}>{members.unique_guests || 0}</p>
                  <p className="text-[10px] text-stone-500">Unique Guests</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-green-50">
                  <p className="text-xl font-bold text-green-700">{members.returning || 0}</p>
                  <p className="text-[10px] text-stone-500">Returning</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-blue-50">
                  <p className="text-xl font-bold text-blue-700">{members.new_guests || 0}</p>
                  <p className="text-[10px] text-stone-500">New Guests</p>
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-stone-400 font-semibold mb-2">Tier Breakdown</p>
                <div className="flex gap-2">
                  <Badge style={{ backgroundColor: "#D4AF3720", color: "#B8860B", border: "1px solid #D4AF37" }} className="text-xs">Gold: {tiers.gold || 0}</Badge>
                  <Badge style={{ backgroundColor: "#C0C0C020", color: "#6B7280", border: "1px solid #A8A8A8" }} className="text-xs">Silver: {tiers.silver || 0}</Badge>
                  <Badge style={{ backgroundColor: "#CD7F3220", color: "#A0522D", border: "1px solid #CD7F32" }} className="text-xs">Bronze: {tiers.bronze || 0}</Badge>
                  <Badge variant="outline" className="text-xs text-stone-500">None: {tiers.none || 0}</Badge>
                </div>
              </div>
            </div>

            {/* Server Analytics */}
            <div className="bg-white rounded-lg border border-stone-200 p-5" data-testid="server-analytics">
              <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-4 flex items-center gap-2">
                <UsersFour size={16} weight="duotone" /> Server Analytics
              </h3>
              {servers.length === 0 ? (
                <p className="text-sm text-stone-400 text-center py-4">No server data</p>
              ) : (
                <div className="space-y-2">
                  {servers.map((srv, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2.5 rounded-lg border border-stone-100">
                      <div className="w-7 text-center">
                        <span className="font-bold text-sm" style={{ color: idx === 0 ? "#B8860B" : idx === 1 ? "#A8A8A8" : idx === 2 ? "#CD7F32" : "#9CA3AF" }}>
                          {idx === 0 ? <Medal size={16} weight="fill" /> : `#${idx + 1}`}
                        </span>
                      </div>
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0" style={{ backgroundColor: srv.color }}>
                        {srv.server_name?.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-stone-700">{srv.server_name}</p>
                        <p className="text-[10px] text-stone-400">{srv.tables} tables</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold" style={{ color: "#2F4F4F" }}>{srv.covers} covers</p>
                        <p className="text-[10px] text-stone-400">{srv.turns} turns</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Table Analytics */}
          <div className="bg-white rounded-lg border border-stone-200 p-5" data-testid="table-analytics">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-4 flex items-center gap-2">
              <Table size={16} weight="duotone" /> Table Analytics
            </h3>
            {tables.length === 0 ? (
              <p className="text-sm text-stone-400 text-center py-4">No table data</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {tables.filter((t) => t.turns > 0 || t.avg_seat_time > 0).map((t, idx) => (
                  <div key={idx} className="p-3 rounded-lg border border-stone-100 hover:bg-stone-50 transition-colors">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm text-stone-700">{t.table_name}</span>
                      <span className="text-[10px] text-stone-400">{t.seats} seats</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span style={{ color: "#2F4F4F" }} className="font-semibold">{t.turns} turns</span>
                      {t.avg_seat_time > 0 && <span className="text-stone-500">{t.avg_seat_time}m avg</span>}
                    </div>
                  </div>
                ))}
                {tables.filter((t) => t.turns > 0 || t.avg_seat_time > 0).length === 0 && (
                  <p className="text-sm text-stone-400 col-span-full text-center py-2">No table activity recorded</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
